import { Box, Grid } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Code";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import _ from "lodash";
import React, { useState } from "react";
import { useAlert } from "react-alert";

import AutocompletePropField from "../../../AutocompletePropField";
import { CodeEditorModal } from "../../../CodeEditorModal";
import { native } from "./EventEditor";
import { MemoTextField } from "../../../EventsPage";
import { EventSnapshot } from "../../../types/global";

const snapshotStyles = makeStyles(() => ({
  snapshot: {
    marginBottom: 16,
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  bold: {
    fontWeight: 550,
  },
}));

export type SnapshotProps = {
  eventType: string;
  snapshot: EventSnapshot;
  index: number;
  onChange: (index: number, snapshot: EventSnapshot) => void;
  onDelete: (index: number) => void;
  isNetworkDynProp: (snapshot: EventSnapshot) => boolean;
  hasNetworkEventEditPerms: () => boolean;
  hasJsDynPropEditPerms: () => boolean;
};

export const Snapshot = function Snapshot({
  eventType,
  snapshot,
  index,
  onChange,
  onDelete,
  isNetworkDynProp,
  hasNetworkEventEditPerms,
  hasJsDynPropEditPerms,
}: SnapshotProps) {
  let codeEditorPrompt: string;
  if (snapshot.type === "js_code") {
    codeEditorPrompt = `Provide only a function body ending with a return statement.

 This variable is available in your code:

   var event; // The DOM event, if available (may be null)

 Example:
   if (event.altKey) {
     return 'SUPPRESS_EVENT';
   }
   return ('x:' + event.clientX + ',y:' + event.clientY + ')';

 Returning 'SUPPRESS_EVENT', as shown above, suppresses the event
 from being delivered to destinations.

`;
  } else if (snapshot.type === "css_selector") {
    codeEditorPrompt = `Enter a CSS Selector.

`;
  } else {
    codeEditorPrompt = `Enter a dot-notation path.

 Examples:
   .$request_headers.x-reddit-session
   .$response.purch_info.amt
   .$query_string_params.region

`;
  }

  const classes = snapshotStyles();
  const alert = useAlert();

  const [codeEditorModalOpen, setCodeEditorModalOpen] = useState(false);

  function handleCodeEditorModalSave(value: string) {
    handlePropertyChange("value", value);
    setCodeEditorModalOpen(false);
  }

  function handleCodeEditorModalCancel() {
    setCodeEditorModalOpen(false);
  }

  function handlePropertyChange<T extends keyof EventSnapshot>(
    prop: T,
    value: EventSnapshot[T]
  ) {
    const newSnapshot = _.extend({}, snapshot, { [prop]: value });
    if (prop === "type") {
      if (isNetworkDynProp(newSnapshot) && !hasNetworkEventEditPerms()) {
        alert.error(
          "You do not have permissions to create Network-specific dynamic properties."
        );
        return;
      }
      if (value === "js_code" && !hasJsDynPropEditPerms()) {
        alert.error(
          "You do not have permissions to create JavaScript dynamic properties."
        );
        return;
      }
    }
    onChange(index, newSnapshot);
  }

  // allowEdit - if editing allowed on current snapshot, based on user types, perms.
  function allowEdit(snapshot: EventSnapshot): boolean {
    if (isNetworkDynProp(snapshot)) {
      return hasNetworkEventEditPerms();
    }
    if (snapshot.type === "js_code") {
      return hasJsDynPropEditPerms();
    }
    return true;
  }

  return (
    <>
      <Grid
        container
        spacing={1}
        className={classes.snapshot}
        id="event-library-snapshot-wrapper"
      >
        <Grid item xs={3}>
          <MemoTextField
            fullWidth
            autoComplete="off"
            label="Property Name"
            name="dynamic-property-name"
            onChange={(e) => handlePropertyChange("name", e.target.value)}
            InputLabelProps={{ shrink: true }}
            value={snapshot.name}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          />
        </Grid>
        <Grid item xs={3}>
          <MemoTextField
            fullWidth
            label="Type"
            name="type"
            onChange={(e) => handlePropertyChange("type", e.target.value)}
            select
            SelectProps={native}
            value={snapshot.type}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          >
            <option value="css_selector">CSS Selector</option>
            <option value="js_code">JavaScript</option>
            {eventType === "network" && (
              <>
                <option value="network">Network</option>
              </>
            )}
          </MemoTextField>
        </Grid>
        <Grid item xs style={{ position: "relative" }}>
          <AutocompletePropField
            propName={snapshot.name}
            fullWidth
            label="Value"
            name="value"
            onChange={(value) => handlePropertyChange("value", value)}
            value={snapshot.value}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          />
          <Box
            component="button"
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            <EditIcon onClick={() => setCodeEditorModalOpen(true)} />
          </Box>
        </Grid>

        <Grid className={clsx(classes.closeIcon)} item xs={1}>
          <CloseIcon onClick={() => onDelete(index)} />
        </Grid>
      </Grid>
      {codeEditorModalOpen && (
        <CodeEditorModal
          onCancel={handleCodeEditorModalCancel}
          onSave={handleCodeEditorModalSave}
          initialCode={snapshot.value}
          textPrompt={codeEditorPrompt}
          saveEnabled={allowEdit(snapshot)}
          parentElementID="event-library-snapshot-wrapper"
        />
      )}
    </>
  );
};
